import React, { useId } from "react";
import {
  Flex,
  Box,
  type SpaceProps,
  type LayoutProps,
  type AspectRatioProps,
  AspectRatio,
  Icon,
} from "@chakra-ui/react";
import { DecorativeBackgroundImage } from "components/images";
import { TextTitle, type TextTitleProps } from "ui/text";
import { type StaticImageData } from "next/image";
import { type BrandColorToken } from "theme/colors";
import { Button } from "ui/button";
import { Link } from "ui/link";

export type VerticalFeatureCardProps = {
  /** The background color of the card. */
  bgColor: BrandColorToken;

  /** If included, it will determine the color of the spotlight gradient. Must be a hex value. */
  spotlightColor?: string;

  /** The image, positioned above the text content. */
  image: StaticImageData;

  /** The aspect ratio of the image. */
  imageRatio: AspectRatioProps["ratio"];

  /** The url to link to. */
  href: string;

  /** The text of the button. */
  buttonText: string;
} & SpaceProps &
  LayoutProps &
  Pick<TextTitleProps, "title" | "description">;

/**
 * `VerticalFeatureCard` is a dark themed card featuring a media element positioned above of text content.
 */
export function VerticalFeatureCard({
  bgColor,
  spotlightColor,
  title,
  description,
  href,
  buttonText,
  image,
  imageRatio,
  ...props
}: VerticalFeatureCardProps) {
  return (
    <Link
      variant="shadowCard"
      href={href}
      bg={bgColor}
      borderRadius="biggest"
      boxShadow="card"
      flexDir="column"
      gap="small"
      maxW={{ base: "100%", sm: "cardMaxW", md: "100%" }}
      mx="auto"
      mb="big"
      overflow="hidden"
      pos="relative"
      w="100%"
      {...props}
    >
      {spotlightColor && <SpotlightGradient color={spotlightColor} />}

      <AspectRatio ratio={imageRatio} w="100%" maxH={{ base: "none", lg: "200px" }}>
        <DecorativeBackgroundImage
          src={image}
          style={{
            objectFit: "cover",
            objectPosition: "top center",
          }}
        />
      </AspectRatio>

      <Flex flexDir="column" gap="smaller" pos="relative" px="big" pb="bigger" flex={1} w="100%">
        <TextTitle
          size="xs"
          theme="dark"
          titleAs="h3"
          title={title}
          description={description}
          actions={
            <Button hasRightChevron as="div">
              {buttonText}
            </Button>
          }
          mt="auto"
        />
      </Flex>
    </Link>
  );
}

function SpotlightGradient({ color }: { color: string }) {
  const id = useId();

  return (
    <Box pos="absolute" inset={0} top="auto">
      <Icon width="100%" height="auto" fill="none" viewBox="0 0 424 240">
        <g clipPath={`url(#a-${id})`} filter={`url(#b-${id})`}>
          <circle cx="212" cy="340" r="292" fill={`url(#c-${id})`} />
        </g>
        <defs>
          <radialGradient
            id={`c-${id}`}
            cx="0"
            cy="0"
            r="1"
            gradientTransform="rotate(90 -64 276)scale(292)"
            gradientUnits="userSpaceOnUse"
          >
            <Box as="stop" stopColor={color} />
            <Box as="stop" offset="1" stopColor={color} stopOpacity="0" />
          </radialGradient>
          <clipPath id={`a-${id}`}>
            <Box as="path" fill="#fff" d="M0 0h424v240H0z" />
          </clipPath>
          <filter
            id={`b-${id}`}
            width="644"
            height="644"
            x="-110"
            y="18"
            colorInterpolationFilters="sRGB"
            filterUnits="userSpaceOnUse"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
            <feGaussianBlur result="effect1_foregroundBlur_9227_1948" stdDeviation="15" />
          </filter>
        </defs>
      </Icon>
    </Box>
  );
}
